import ModuleGateBox from '@/components/ModuleGateBox';
import { useAccess } from '@/hooks/useAccess';
import useMenu from '@/hooks/useMenu';
import useMenuBadges from '@/hooks/useMenuBadges';
import { useIntl } from '@/i18n';
import Forbidden from '@/pages/403';
import BusinessLanguagesProvider from '@/providers/BusinessLanguagesProvider';
import { useAppStore } from '@/store/app';
import { useAuthStore } from '@/store/auth';
import { useModuleGateStore } from '@/store/moduleGate';
import { isModuleGated, moduleOfPath } from '@/utils/moduleGate';
import { Grid, Layout } from 'antd';
import { useEffect, useState } from 'react';
import { Outlet, useLocation, useMatches } from 'react-router';
import BusinessLanguageBanner from './components/BusinessLanguageBanner';
import ModuleRail from './components/ModuleRail';
import SideMenu from './components/SideMenu';

import BaseDrawer from '@/components/base/BaseDrawer';
import AdminHeader from './components/AdminHeader';
import SelectLang from './components/LanguageSelect';
import MobileNavigation from './components/MobileNavigation';
import PlanCapacity from './components/PlanCapacity';

/**
 * 基础布局（antd Layout）：侧边栏 + 顶栏 + 内容区
 * 同时承担登录态与路由权限守卫（对应 vue-naive-admin 的 permission-guard）
 */
export default function AdminLayout() {
  // 按字段订阅（zustand 的 action 引用是稳定的）
  const tenantName = useAuthStore((state) => state.tenantName);
  const authStatus = useAuthStore((state) => state.status);
  const gateRows = useModuleGateStore((state) => state.rows);
  const collapsed = useAppStore((state) => state.collapsed);
  const dark = useAppStore((state) => state.dark);
  const toggleCollapsed = useAppStore((state) => state.toggleCollapsed);
  const updateSettings = useAppStore((state) => state.updateSettings);
  const { canEnter, can } = useAccess();
  const { breadcrumbs, menuTree } = useMenu();
  const intl = useIntl();
  const matches = useMatches();
  const location = useLocation();
  const menuBadges = useMenuBadges(location.pathname);
  const screens = Grid.useBreakpoint();
  const [activeModule, setActiveModule] = useState<string>();
  const [menuOpen, setMenuOpen] = useState(false);
  const isMobile = screens.lg === false;
  useEffect(() => {
    if (!isMobile) setMenuOpen(false);
  }, [isMobile]);

  useEffect(() => {
    document.title = tenantName ?? '';
    return () => {
      document.title = '';
    };
  }, [tenantName]);

  // 门控数据跟着登录态取：未登录不问，登录后取一次（store 内做在飞去重，重挂不会重发）
  useEffect(() => {
    if (authStatus === 'authed') void useModuleGateStore.getState().load();
  }, [authStatus]);

  const routeModule = breadcrumbs.find((node) => node.kind === 'module')?.code;
  useEffect(() => {
    // 只有路由切换才对齐模块，菜单数据刷新不覆盖用户正在浏览的P2。
    setActiveModule(routeModule);
  }, [location.pathname, routeModule]);

  const selectModule = (code: string) => {
    setActiveModule(code);
    updateSettings({ collapsed: false });
  };

  // 小屏自动收起侧边栏（跨断点时才会覆盖用户的手动状态）
  useEffect(() => {
    if (screens.lg === undefined) return;
    // 后台标签页等场景 innerWidth 会是 0，此时断点结果不可信，
    // 不能拿它去覆盖（并持久化）用户的折叠状态
    if (!window.innerWidth) return;
    updateSettings({ collapsed: !screens.lg });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screens.lg]);

  // 登录态由 AuthGuard 负责，这里只做路由权限校验（独立于菜单的第二层校验）：
  // 匹配链路上任一 handle.access 未命中则渲染 403。
  //
  // 用 canEnter 而不是直接查 authStore.menus：判据要与侧边栏菜单（useMenu）、
  // 按钮级权限（useAccess().can）统一走同一份「有没有这颗键」的逻辑，不能自己再
  // 造一套判断（见 CLAUDE.md「权限判断复用 useAccess，不自己造判断逻辑」）。
  // 超管由 canEnter 里的通配放行，这里不必单开一条分支。
  //
  // 没挂 handle.access 的路由（工作台、等后端接口的占位页、以及 5 条与 PRD 对不上被刻意
  // 暴露的路由——见 src/router/index.tsx 顶部说明）一律放行，这里的 !code 分支
  // 就是干这个的。真闸是网关与服务端的权限拦截，这里只负责别让人点进明确无权的页。
  //
  // 页面码（如 `org.notice.view`）由 iam 算进可达路由，前端只认 `reachableRoutes` 译出的
  // 菜单键；被挡下时把那个菜单键交给 403 页，页面级 403 没有接口响应，用它对上「哪一页」。
  //
  // 子页（`type/settings`、`club-level/settings` 等）还有第二个身份：独立设置包的**动作码**，
  // 它永远不会出现在 `menus` 里（那是菜单键集合）。所以 handle 另开一档 `actionAccess`，
  // 用 `can` 判服务端下发的动作码集合；两档满足其一即可进。
  const deniedKey = matches.reduce<string | undefined>((denied, item) => {
    if (denied) return denied;
    const handle = item.handle as
      | { access?: string | string[]; actionAccess?: string | string[] }
      | undefined;
    const access = handle?.access;
    const actionAccess = handle?.actionAccess;
    if (!access && !actionAccess) return undefined;
    if (access && canEnter(access)) return undefined;
    if (actionAccess && can(actionAccess)) return undefined;
    if (!access)
      return Array.isArray(actionAccess) ? actionAccess[0] : actionAccess;
    return Array.isArray(access) ? access[0] : access;
  }, undefined);
  const accessible = !deniedKey;

  // 门控在权限之前（PRD 27823）：套餐没买的模块，连「你没有权限」都不该提——
  // 那会让人以为加个岗位就能用。先看这一页属于哪个模块，锁着就直接出拦截页。
  // `/home`、`/profile`、`/iframe` 这类不归任何模块的页面不判（moduleOfPath 回 undefined）。
  const gatedModule = moduleOfPath(location.pathname);
  const moduleBlocked = isModuleGated(gateRows, gatedModule);

  const activeNode = menuTree.find(
    (node) => node.kind === 'module' && node.code === activeModule,
  );

  return (
    <BusinessLanguagesProvider>
      <Layout className="admin-layout">
        <AdminHeader
          isMobile={isMobile}
          moduleTitle={activeNode?.title}
          collapsed={collapsed}
          onToggleCollapsed={toggleCollapsed}
          menuOpen={menuOpen}
          onMenuOpen={() => setMenuOpen(true)}
        />
        <Layout className="admin-layout-body">
          {!isMobile && (
            <Layout.Sider
              width={80}
              theme={dark ? 'dark' : 'light'}
              className="admin-module-sider"
            >
              <div
                data-testid="admin-module-sider"
                className="h-full flex flex-col"
              >
                <ModuleRail
                  activeModule={activeModule}
                  currentModule={routeModule}
                  onSelectModule={selectModule}
                  badges={menuBadges}
                />
              </div>
            </Layout.Sider>
          )}
          {!isMobile && (
            <Layout.Sider
              id="admin-submenu"
              width={210}
              collapsedWidth={0}
              collapsed={collapsed || !activeModule}
              trigger={null}
              theme={dark ? 'dark' : 'light'}
              className="admin-submenu-sider"
            >
              <div
                data-testid="admin-sub-menu"
                className="admin-submenu-scroll py-5 px-3 min-h-0 flex-1 overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable]"
                inert={collapsed || !activeModule}
              >
                <SideMenu
                  moduleCode={activeModule || '__empty__'}
                  badges={menuBadges}
                />
              </div>
              {activeNode && (
                <div className="admin-desktop-plan shrink-0 p-3">
                  <PlanCapacity />
                </div>
              )}
            </Layout.Sider>
          )}
          <Layout.Content className="admin-content">
            {/* key 变化时重新挂载：换路由触发一次进入动画；换语言让当前页按新语言
                重新取数——服务端给的人话文本（审计动作名、申请/订单类型名）是按
               请求头 Accept-Language 拼好的，只重渲染不会重发请求，会一直停在旧语言。
               请求头取自 localStorage，而 setLocale 已同步写盘，所以重挂后的请求就是新语言。 */}
            <div
              key={`${location.pathname}|${intl.locale}`}
              className="admin-shell page-transition"
            >
              <BusinessLanguageBanner />
              {moduleBlocked && gatedModule ? (
                <ModuleGateBox moduleCode={gatedModule} />
              ) : accessible ? (
                <Outlet />
              ) : (
                <Forbidden menuKey={deniedKey} />
              )}
            </div>
          </Layout.Content>
        </Layout>
        {isMobile && (
          <BaseDrawer
            open={menuOpen}
            onClose={() => setMenuOpen(false)}
            title={intl.formatMessage({ id: 'layout.navigation' })}
            placement="left"
            size={320}
            className="admin-mobile-menu admin-submenu-sider"
            rootClassName="admin-mobile-navigation"
            footer={<PlanCapacity onNavigate={() => setMenuOpen(false)} />}
          >
            <div className="admin-drawer-language flex items-center gap-3 pt-4 px-3 pb-0 text-[13px]">
              <span>{intl.formatMessage({ id: 'layout.language' })}</span>
              <SelectLang />
            </div>
            <div
              id="admin-mobile-navigation"
              className="admin-submenu-scroll py-5 px-3 min-h-0 flex-1 overflow-y-auto overflow-x-hidden [scrollbar-gutter:stable]"
            >
              <MobileNavigation
                badges={menuBadges}
                onSelect={() => setMenuOpen(false)}
              />
            </div>
          </BaseDrawer>
        )}
      </Layout>
    </BusinessLanguagesProvider>
  );
}
